import { useState } from 'react';
import { Copy, Link2, Mail, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';
import { useMe } from '@/lib/queries';

/**
 * Account utilities: send yourself a test email (proves mail delivery) and —
 * for admins — mint an instant-share link that logs a recipient straight in.
 */
export function AccountPage() {
  const { data: me } = useMe();
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyShare, setBusyShare] = useState(false);
  const [busyTest, setBusyTest] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function makeShareLink() {
    setBusyShare(true);
    setShareLink(null);
    setCopied(false);
    try {
      const { link } = await api.createShareLink();
      setShareLink(link);
    } catch (err) {
      setShareLink(null);
      setTestMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Failed to create.' });
    } finally {
      setBusyShare(false);
    }
  }

  async function copy() {
    if (!shareLink) return;
    await navigator.clipboard.writeText(shareLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function sendTest() {
    setBusyTest(true);
    setTestMsg(null);
    try {
      await api.sendTestEmail();
      setTestMsg({ ok: true, text: `Test email sent to ${me?.email ?? 'you'} (if sending is enabled).` });
    } catch (err) {
      setTestMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Send failed.' });
    } finally {
      setBusyTest(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-lg font-semibold text-strong">Account</h1>
        <p className="text-sm text-muted">{me?.name} · {me?.email ?? 'no email on file'} {me?.isAdmin && '· Administrator'}</p>
      </div>

      {/* Test email */}
      <section className="rounded-xl border line surface-card p-5">
        <div className="mb-1 flex items-center gap-2">
          <Mail className="size-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-strong">Test email delivery</h2>
        </div>
        <p className="mb-3 text-[13px] text-muted">Sends a test message to your email — confirms delivery works.</p>
        <Button variant="secondary" loading={busyTest} onClick={sendTest} disabled={!me?.email}>Send test email to me</Button>
        {testMsg && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-[13px] ${testMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{testMsg.text}</p>
        )}
      </section>

      {/* Share link (admin) */}
      {me?.isAdmin && (
        <section className="rounded-xl border line surface-card p-5">
          <div className="mb-1 flex items-center gap-2">
            <Link2 className="size-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-strong">Instant access link</h2>
          </div>
          <p className="mb-3 text-[13px] text-muted">
            Generates a one-time link that logs the recipient straight in as <strong>{me.username}</strong> — for quick sharing. The link is single-use and expires.
          </p>
          <Button variant="primary" loading={busyShare} onClick={makeShareLink}>Generate access link</Button>
          {shareLink && (
            <div className="mt-3 flex items-center gap-2 rounded-lg surface-sunken p-2">
              <code className="min-w-0 flex-1 truncate px-1 text-[12px] text-body">{shareLink}</code>
              <Button variant="ghost" className="h-8 shrink-0" onClick={copy}>
                <Copy className="size-3.5" /> {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          )}
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-faint">
            <ShieldCheck className="size-3.5" /> Only share with someone you want to give full access.
          </p>
        </section>
      )}
    </div>
  );
}
